import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, DollarSign, MapPin, Tag, Star, Receipt, Camera } from 'lucide-react';

const CATEGORIES = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'repair', label: 'Repair' },
  { value: 'tire', label: 'Tire' },
  { value: 'registration', label: 'Registration' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'parts', label: 'Parts' },
  { value: 'accessory', label: 'Accessory' },
  { value: 'other', label: 'Other' },
];

const AddReceiptDialog = ({ 
  open, 
  onClose, 
  vehicleId,
  currentMileage,
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    vendor_name: '',
    category: 'maintenance',
    mileage: currentMileage || '',
    notes: '',
    rating: 0,
    is_reimbursed: false,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.vendor_name) {
      return;
    }

    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount),
      mileage: formData.mileage ? parseInt(formData.mileage) : null,
      rating: formData.rating || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Add Receipt</DialogTitle>
              <p className="text-sm text-slate-500">Track your vehicle expenses</p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Date and Amount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-teal-600" />
                Date
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="mt-1"
                required
                data-testid="receipt-date-input"
              />
            </div>
            <div>
              <Label htmlFor="amount" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-teal-600" />
                Amount
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                className="mt-1"
                required
                data-testid="receipt-amount-input"
              />
            </div>
          </div>

          {/* Vendor Name */}
          <div>
            <Label htmlFor="vendor_name" className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-teal-600" />
              Store or Service Provider
            </Label>
            <Input
              id="vendor_name"
              type="text"
              placeholder="e.g. AutoZone, Joe's Garage"
              value={formData.vendor_name}
              onChange={(e) => handleChange('vendor_name', e.target.value)}
              className="mt-1"
              required
              data-testid="receipt-vendor-input"
            />
          </div>

          {/* Category and Mileage */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="category" className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-teal-600" />
                Category
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) => handleChange('category', value)}
              >
                <SelectTrigger className="mt-1" data-testid="receipt-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="mileage">Mileage (optional)</Label>
              <Input
                id="mileage"
                type="number"
                placeholder={currentMileage ? `Current: ${currentMileage.toLocaleString()}` : ''}
                value={formData.mileage}
                onChange={(e) => handleChange('mileage', e.target.value)}
                className="mt-1"
                data-testid="receipt-mileage-input"
              />
            </div>
          </div>

          {/* Rating */}
          <div>
            <Label className="flex items-center gap-2">
              <Star className="h-4 w-4 text-teal-600" />
              Rating (optional)
            </Label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleChange('rating', formData.rating === star ? 0 : star)}
                  className="p-1"
                >
                  <Star 
                    className={`h-6 w-6 transition-colors ${
                      star <= formData.rating 
                        ? 'text-amber-400 fill-amber-400' 
                        : 'text-slate-300'
                    }`} 
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Reimbursement Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div>
              <Label htmlFor="is_reimbursed" className="font-medium">Mark as Reimbursed</Label>
              <p className="text-xs text-slate-500">This expense was reimbursed</p>
            </div>
            <Switch
              id="is_reimbursed"
              checked={formData.is_reimbursed}
              onCheckedChange={(checked) => handleChange('is_reimbursed', checked)}
              data-testid="receipt-reimbursed-toggle"
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any additional details..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="mt-1"
              rows={2}
              data-testid="receipt-notes-input"
            />
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            onClick={handleSubmit}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="submit-receipt-btn"
          >
            <Receipt className="h-4 w-4 mr-2" />
            Add Receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddReceiptDialog;
